import { useEffect, useRef, useState } from 'react';
import { rechercherVilles } from '../services/rechercheService';
import '../assets/styles/Mentions.css';

/**
 * Liste de choix des villes de France, à la place d'un champ texte.
 *
 * On tape le début du nom, la liste officielle des communes répond : choisir
 * une ligne pose le nom exact de la commune (« Lyon », « Saint-Étienne »), ce
 * qui évite les variantes de saisie (« lyon 3 », « St Etienne »). Une ville
 * déjà enregistrée s'affiche telle quelle, même si elle n'est pas dans la
 * liste ; on peut la remplacer en tapant autre chose.
 *
 * Si la liste officielle est injoignable, le champ redevient une saisie libre :
 * un formulaire ne reste jamais bloqué faute de réseau.
 *
 * Même contrat que les autres champs : `value`, `onChange(e)` avec
 * `e.target.value`. `onChoisir({ nom, codePostal })` reçoit la commune choisie
 * (`codePostal` quand elle n'en a qu'un) pour renseigner le champ voisin.
 */

const LONGUEUR_MIN = 2;
const DELAI_MS = 250;

function SelectVille({ value, onChange, onChoisir, disabled, onBlur, ...reste }) {
  const [texte, setTexte] = useState(value || '');
  const [ouvert, setOuvert] = useState(false);
  const [villes, setVilles] = useState([]);
  const [disponible, setDisponible] = useState(true);
  const [chargement, setChargement] = useState(false);
  const [actif, setActif] = useState(0);
  const numero = useRef(0);
  const minuteur = useRef(null);

  // La valeur change de l'extérieur (formulaire rechargé, texte collé) : le champ suit.
  useEffect(() => {
    setTexte(value || '');
  }, [value]);

  useEffect(() => () => clearTimeout(minuteur.current), []);

  function chercher(q) {
    clearTimeout(minuteur.current);
    if (q.trim().length < LONGUEUR_MIN) {
      numero.current += 1;
      setVilles([]);
      setChargement(false);
      return;
    }
    setChargement(true);
    minuteur.current = setTimeout(async () => {
      const mien = ++numero.current;
      try {
        const res = await rechercherVilles(q.trim());
        if (mien !== numero.current) return;
        setVilles(res.data?.villes || []);
        setDisponible(res.data?.disponible !== false);
        setActif(0);
      } catch {
        if (mien !== numero.current) return;
        setVilles([]);
        setDisponible(false);
      } finally {
        if (mien === numero.current) setChargement(false);
      }
    }, DELAI_MS);
  }

  function surSaisie(e) {
    const t = e.target.value;
    setTexte(t);
    setOuvert(true);
    chercher(t);
    // Liste injoignable : saisie libre, la valeur suit la frappe.
    if (!disponible) onChange?.({ target: { value: t } });
  }

  function choisir(ville) {
    setTexte(ville.nom);
    setOuvert(false);
    setVilles([]);
    onChange?.({ target: { value: ville.nom } });
    onChoisir?.({
      nom: ville.nom,
      codePostal: ville.codesPostaux?.length === 1 ? ville.codesPostaux[0] : '',
    });
  }

  function surTouche(e) {
    if (ouvert && villes.length) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActif((a) => (a + 1) % villes.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActif((a) => (a - 1 + villes.length) % villes.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        if (e.key === 'Enter') e.preventDefault();
        choisir(villes[actif]);
      }
    }
    if (e.key === 'Escape') setOuvert(false);
  }

  function surPerte(e) {
    // Laisse le clic sur une proposition se faire avant de refermer.
    setTimeout(() => {
      setOuvert(false);
      // Texte tapé sans choix : on revient à la ville enregistrée, sauf si
      // le champ est vidé (on l'efface) ou la liste injoignable (saisie libre).
      setTexte((courant) => {
        if (!disponible) return courant;
        if (!courant.trim()) {
          if (value) onChange?.({ target: { value: '' } });
          return '';
        }
        return value || '';
      });
    }, 150);
    onBlur?.(e);
  }

  const vide = ouvert && texte.trim().length >= LONGUEUR_MIN && !chargement && !villes.length && disponible;

  return (
    <div className="mentions">
      <input
        type="text"
        autoComplete="off"
        {...reste}
        value={texte}
        disabled={disabled}
        onChange={surSaisie}
        onFocus={() => texte && chercher(texte)}
        onKeyDown={surTouche}
        onBlur={surPerte}
      />
      {ouvert && (villes.length > 0 || vide || chargement) && (
        <div className="mentions-liste" role="listbox">
          {chargement && !villes.length && <div className="mentions-info">Recherche…</div>}
          {vide && <div className="mentions-info">Aucune ville de France ne correspond à « {texte} ».</div>}
          {villes.map((v, i) => (
            <button
              type="button"
              role="option"
              aria-selected={i === actif}
              key={`${v.nom}|${v.codesPostaux?.[0] || ''}`}
              className={`mentions-item${i === actif ? ' mentions-item-actif' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                choisir(v);
              }}
              onMouseEnter={() => setActif(i)}
            >
              <span className="mentions-nom">{v.nom}</span>
              <span className="mentions-detail">
                {[v.departement, v.codesPostaux?.length === 1 ? v.codesPostaux[0] : null]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default SelectVille;
